import CheckIcon from '@mui/icons-material/Check';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import Fuse from 'fuse.js';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';
import type { FC } from 'react';

import type { Countries } from 'constants/countryPreset';
import CountryPresetConst, { countryCodes } from 'constants/countryPreset';

import CustomSearchbar from 'components/CustomSearchBar';
import CustomListItem from 'components/DeviceConfigComponents/CustomListItem';
import ScrollContainerWrapper from 'components/DeviceConfigComponents/ScrollContainerWrapper';

interface SelectCountryProps {
  country: Countries | null;
  setCountry: (country: Countries) => void;
}

const SelectCountry: FC<SelectCountryProps> = props => {
  const { country, setCountry } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const [search, setSearch] = useState<string>('');

  const arrayOfCountryPresets = useMemo(
    () =>
      Object.keys(CountryPresetConst).map(key => ({
        key,
        label: t(`common:countries.${key}`),
        code: countryCodes[key as Countries],
      })),
    [t],
  );

  const fuse = useMemo(() => {
    return new Fuse(arrayOfCountryPresets, {
      keys: ['label', 'code', 'key'],
      threshold: 0.2,
    });
  }, [arrayOfCountryPresets]);

  const filteredCountryPresetIndexes = useMemo((): number[] => {
    if (search === '') {
      return arrayOfCountryPresets.map((_, index) => index);
    }

    return fuse.search(search).map(result => result.refIndex);
  }, [search, fuse, arrayOfCountryPresets]);

  const handleClearSearch = useCallback(() => {
    setSearch('');
  }, []);

  return (
    <ScrollContainerWrapper height="60vh">
      <Box mb={4}>
        <Typography align="center" variant="h2" sx={{ mb: 2 }}>
          {t('common:pages.device_setup.country_setup.title')}
        </Typography>
        <Typography align="center" variant="body1">
          {t('common:pages.device_setup.country_setup.description')}
        </Typography>
      </Box>
      <Box mb={2} width="100%">
        <CustomSearchbar
          hasSearched={!!search}
          search={search}
          setSearch={setSearch}
          handleClearSearch={handleClearSearch}
        />
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        sx={{
          overflow: 'auto',
          paddingBottom: 4,
        }}
      >
        {filteredCountryPresetIndexes.length === 0 && search !== '' ? (
          <Typography variant="body1" textAlign="center">
            {t('common:pages.settings.subpages.grid.no_search_results')}
          </Typography>
        ) : null}
        {filteredCountryPresetIndexes.map((value, index, { length }) => {
          const val = arrayOfCountryPresets[value];
          const key = val.key as Countries;

          return (
            <CustomListItem
              key={key}
              leftComponent={
                <IconButton disableRipple>
                  <CheckIcon
                    sx={{
                      color:
                        country === key
                          ? theme.vars.palette.primary.main
                          : 'transparent',
                    }}
                  />
                </IconButton>
              }
              leftText={val.label}
              isFirstItem={index === 0}
              isLastItem={index === length - 1}
              rightComponent={
                <Typography variant="body1" color="grey.600">
                  {`${CountryPresetConst[key].la1} A`}
                </Typography>
              }
              onClick={(): void => {
                setCountry(key);
              }}
            />
          );
        })}
      </Box>
    </ScrollContainerWrapper>
  );
};

export default SelectCountry;
